import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Subject, throwError } from 'rxjs';
import { TableColumn } from '../../../../component-library/table/table-column';
import { TableConfig } from '../../../../component-library/table/table-config';
import { DashboardWidget } from '../../../../viewmodels/dashboard/dashboard-widget';
import { Widget } from '../../../../viewmodels/dashboard/widget';
import { WidgetParameter } from '../../../../viewmodels/dashboard/widget-parameter';
import { DisplayData } from '../bar/DisplayData';
import { ItemizedWidgetData } from '../itemized-widget-data/itemized-widget-data';
import { WidgetDataType } from '../widget-data-type';
import { WidgetTableData } from '../widget-table-data';

@Injectable({
  providedIn: 'root',
})
export class ViewDataTableService {
  public showItemizedData: Subject<Widget> = new Subject<Widget>();
  public showOverallTable: Subject<DashboardWidget> = new Subject<DashboardWidget>();
  public setOverallTableData: Subject<WidgetTableData> = new Subject<WidgetTableData>();
  public setItemizedDataWidgetParam: Subject<WidgetParameter> = new Subject<WidgetParameter>();
  public setItemizedDataFilterName: Subject<WidgetParameter> = new Subject<WidgetParameter>();

  public initializeTableConfig(): TableConfig {
    const tableConfig = new TableConfig();
    tableConfig.showHeader = false;
    tableConfig.enableAdd = false;
    tableConfig.enableEdit = false;
    tableConfig.enableDelete = false;
    tableConfig.enableFiltering = false;
    tableConfig.enableSorting = false;
    tableConfig.rowsPerPage = 5;
    return tableConfig;
  }

  public setTableColumns(itemizedWidgetData: ItemizedWidgetData, dashboardWidget: DashboardWidget, simpleView: boolean): TableColumn[] {
    let columns: TableColumn[] = [];

    const {
      widget: { widgetDataType },
    } = dashboardWidget;

    const headerColumn = this.createHeaderColumn(itemizedWidgetData, widgetDataType);
    const countColumn = this.createCountColumn();

    columns = [headerColumn, countColumn];

    if (!simpleView) {
      const percentageColumn = this.createPercentageColumn();
      columns.push(percentageColumn);
    }

    return columns;
  }

  public setTableData(tableData: WidgetTableData, widget: Widget, simpleView: boolean): DisplayData[] {
    const displayData = [];
    let count: number = 0;
    let percentage: number = 0;

    for (let i = 0; i < tableData.Labels.length; i++) {
      count += tableData.Data[i];
    }

    for (let i = 0; i < tableData.Labels.length; i++) {
      const data = new DisplayData();
      data.widgetId = widget.widgetId;
      data.label = tableData.Labels[i] ? tableData.Labels[i] : 'None';
      data.count = tableData.Data[i];
      if (tableData.Data[i] > 0 && !simpleView) {
        data.percentage = (tableData.Data[i] / count) * 100;
        percentage += data.percentage;
      } else {
        data.percentage = 0;
      }
      displayData.push(data);
    }

    if (tableData.Labels.length > 0 && !simpleView) {
      const data = new DisplayData();
      data.widgetId = widget.widgetId;
      data.label = 'Total';
      data.count = count;
      data.percentage = percentage;
      displayData.push(data);
    }

    return displayData;
  }

  public showPercentageColumn(widgetDataType: WidgetDataType) {
    return widgetDataType === WidgetDataType.PlannedMaintenanceDistribution || widgetDataType === WidgetDataType.PlannedMaintenanceImprovements;
  }

  private createCountColumn(): TableColumn {
    const countColumnHeader = 'Count';
    const countColumn = new TableColumn(countColumnHeader, 'count');
    return countColumn;
  }

  private createPercentageColumn(): TableColumn {
    const percentageColumn = new TableColumn('%', 'percentage');
    percentageColumn.formatterPipe = DecimalPipe;
    percentageColumn.formatterPipeArgs = ['1.2-2'];
    return percentageColumn;
  }

  private createHeaderColumn(itemizedWidgetData: ItemizedWidgetData, widgetDataType: WidgetDataType): TableColumn {
    let headerColumn = new TableColumn('', 'label');

    switch (widgetDataType) {
    case WidgetDataType.AssetCount:
    case WidgetDataType.OverdueMitigations:
    case WidgetDataType.PlannedMaintenanceDistribution:
    case WidgetDataType.HighRiskItems:
    case WidgetDataType.MitigationsDueByMonth:
    case WidgetDataType.CompletedReports:
    case WidgetDataType.RiskDistribution:
    case WidgetDataType.MitigationsDue:
    case WidgetDataType.PlannedMaintenanceImprovements:
      headerColumn = itemizedWidgetData.createOverallTableHeaderColumn();
      break;
    default:
      throwError('Unknown widget data type');
      return;
    }

    return headerColumn;
  }
}
